/** Tiny API client. One place for the base URL, the session id and auth token. */

const API_BASE = import.meta.env.VITE_API_URL ?? '';

/**
 * Anonymous viewer id, used for ad frequency capping and unique-session counts.
 * Random per browser, stored locally, never sent anywhere but our own API — the
 * server hashes it with a daily salt before it touches the database.
 */
function sessionId(): string {
  const key = 'fl_session';
  let value = localStorage.getItem(key);
  if (!value) {
    value = crypto.randomUUID();
    localStorage.setItem(key, value);
  }
  return value;
}

const TOKEN_KEY = 'fl_token';
export const getToken = (): string | null => localStorage.getItem(TOKEN_KEY);
export const setToken = (token: string | null): void => {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
};

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code: string,
    readonly details?: unknown,
  ) {
    super(message);
  }
  /** A 402 carries the feature and the plans that include it. */
  get isEntitlement(): boolean {
    return this.status === 402;
  }
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'x-fl-session': sessionId(),
      ...(init.body ? { 'content-type': 'application/json' } : {}),
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(init.headers ?? {}),
    },
  });

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const body = text ? (JSON.parse(text) as unknown) : null;

  if (!res.ok) {
    const err = (body as { error?: { message?: string; code?: string; details?: unknown } } | null)?.error;
    throw new ApiError(res.status, err?.message ?? `Request failed (${res.status})`, err?.code ?? 'error', err?.details);
  }
  return body as T;
}

export const post = <T>(path: string, body: unknown): Promise<T> =>
  api<T>(path, { method: 'POST', body: JSON.stringify(body) });

export const patch = <T>(path: string, body: unknown): Promise<T> =>
  api<T>(path, { method: 'PATCH', body: JSON.stringify(body) });

/** sendBeacon-friendly fire-and-forget, for unload-time analytics. */
export function beacon(path: string, body: unknown): void {
  const payload = JSON.stringify(body);
  if (navigator.sendBeacon) {
    navigator.sendBeacon(`${API_BASE}${path}`, new Blob([payload], { type: 'application/json' }));
    return;
  }
  void fetch(`${API_BASE}${path}`, { method: 'POST', body: payload, headers: { 'content-type': 'application/json' }, keepalive: true });
}

export const streamUrl = (path: string): string => `${API_BASE}${path}`;
